import { useState, useRef, useEffect, useMemo } from "react";
import { formToOverrides, generateFallbackText } from "../helpers";
import clsx from "clsx";
import translate from "../locales";
import generator from "../generator";

const DEFAULT_PAGE = 1;
const PAGE_COUNT = 5;

export function MultiPoster({ data, className, allowHide = false }) {
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [hidden, setHidden] = useState(false);
  const overrides = useMemo(() => formToOverrides(data), [data]);
  const fallbackText = useMemo(
    () => generateFallbackText(page, data),
    [page, data],
  );
  const canvasRef = useRef(null);
  useEffect(() => {
    // We generate the poster two times
    // This fixes an issue, where chromium-based browsers don't use the custom fonts on the first attempt
    generator.modifyCanvas(canvasRef.current, {
      ...overrides,
      layout: page.toString(),
    });
  }, [page, overrides]);

  const increasePage = () => setPage((page) => Math.min(page + 1, PAGE_COUNT));
  const decreasePage = () => setPage((page) => Math.max(1, page - 1));

  return (
    <div
      className={clsx(
        "post",
        { "post--hidden": hidden, "post--allow-hide": allowHide },
        className,
      )}
    >
      <div className="post__canvas-wrapper">
        <canvas
          ref={canvasRef}
          className="post__canvas"
          aria-labelledby="canvasLabel"
        >
          <span aria-live="polite" id="canvasLabel">
            {fallbackText}
          </span>
        </canvas>
      </div>

      <div className={clsx("post__footer")}>
        {allowHide && (
          <button
            className="post__button post__button--hide"
            onClick={() => setHidden((v) => !v)}
          >
            <svg
              className="post__icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentcolor"
              aria-label={
                hidden ? "poster.nav.showPoster" : "poster.nav.hidePoster"
              }
            >
              {hidden ? (
                <path d="M16.1 13.2998L14.65 11.8498C14.8 11.0665 14.575 10.3331 13.975 9.64981C13.375 8.96647 12.6 8.69981 11.65 8.84981L10.2 7.39981C10.4833 7.26647 10.7708 7.16647 11.0625 7.0998C11.3542 7.03314 11.6667 6.9998 12 6.9998C13.25 6.9998 14.3125 7.4373 15.1875 8.3123C16.0625 9.1873 16.5 10.2498 16.5 11.4998C16.5 11.8331 16.4667 12.1456 16.4 12.4373C16.3333 12.729 16.2333 13.0165 16.1 13.2998ZM19.3 16.4498L17.85 15.0498C18.4833 14.5665 19.0458 14.0373 19.5375 13.4623C20.0292 12.8873 20.45 12.2331 20.8 11.4998C19.9667 9.81647 18.7708 8.47897 17.2125 7.48731C15.6542 6.49564 13.9167 5.9998 12 5.9998C11.5167 5.9998 11.0417 6.03314 10.575 6.0998C10.1083 6.16647 9.65 6.26647 9.2 6.39981L7.65 4.8498C8.33333 4.56647 9.03333 4.35397 9.75 4.2123C10.4667 4.07064 11.2167 3.9998 12 3.9998C14.5167 3.9998 16.7583 4.69564 18.725 6.08731C20.6917 7.47897 22.1167 9.28314 23 11.4998C22.6167 12.4831 22.1125 13.3956 21.4875 14.2373C20.8625 15.079 20.1333 15.8165 19.3 16.4498ZM19.8 22.5998L15.6 18.4498C15.0167 18.6331 14.4292 18.7706 13.8375 18.8623C13.2458 18.954 12.6333 18.9998 12 18.9998C9.48333 18.9998 7.24167 18.304 5.275 16.9123C3.30833 15.5206 1.88333 13.7165 1 11.4998C1.35 10.6165 1.79167 9.79564 2.325 9.03731C2.85833 8.27897 3.46667 7.5998 4.15 6.9998L1.4 4.1998L2.8 2.7998L21.2 21.1998L19.8 22.5998ZM5.55 8.39981C5.06667 8.83314 4.625 9.30814 4.225 9.82481C3.825 10.3415 3.48333 10.8998 3.2 11.4998C4.03333 13.1831 5.22917 14.5206 6.7875 15.5123C8.34583 16.504 10.0833 16.9998 12 16.9998C12.3333 16.9998 12.6583 16.979 12.975 16.9373C13.2917 16.8956 13.6167 16.8498 13.95 16.7998L13.05 15.8498C12.8667 15.8998 12.6917 15.9373 12.525 15.9623C12.3583 15.9873 12.1833 15.9998 12 15.9998C10.75 15.9998 9.6875 15.5623 8.8125 14.6873C7.9375 13.8123 7.5 12.7498 7.5 11.4998C7.5 11.3165 7.5125 11.1415 7.5375 10.9748C7.5625 10.8081 7.6 10.6331 7.65 10.4498L5.55 8.39981Z" />
              ) : (
                <path d="M12 16C13.25 16 14.3125 15.5625 15.1875 14.6875C16.0625 13.8125 16.5 12.75 16.5 11.5C16.5 10.25 16.0625 9.1875 15.1875 8.3125C14.3125 7.4375 13.25 7 12 7C10.75 7 9.6875 7.4375 8.8125 8.3125C7.9375 9.1875 7.5 10.25 7.5 11.5C7.5 12.75 7.9375 13.8125 8.8125 14.6875C9.6875 15.5625 10.75 16 12 16ZM12 14.2C11.25 14.2 10.6125 13.9375 10.0875 13.4125C9.5625 12.8875 9.3 12.25 9.3 11.5C9.3 10.75 9.5625 10.1125 10.0875 9.5875C10.6125 9.0625 11.25 8.8 12 8.8C12.75 8.8 13.3875 9.0625 13.9125 9.5875C14.4375 10.1125 14.7 10.75 14.7 11.5C14.7 12.25 14.4375 12.8875 13.9125 13.4125C13.3875 13.9375 12.75 14.2 12 14.2ZM12 19C9.56667 19 7.35 18.3208 5.35 16.9625C3.35 15.6042 1.9 13.7833 1 11.5C1.9 9.21667 3.35 7.39583 5.35 6.0375C7.35 4.67917 9.56667 4 12 4C14.4333 4 16.65 4.67917 18.65 6.0375C20.65 7.39583 22.1 9.21667 23 11.5C22.1 13.7833 20.65 15.6042 18.65 16.9625C16.65 18.3208 14.4333 19 12 19ZM12 17C13.8833 17 15.6125 16.5042 17.1875 15.5125C18.7625 14.5208 19.9667 13.1833 20.8 11.5C19.9667 9.81667 18.7625 8.47917 17.1875 7.4875C15.6125 6.49583 13.8833 6 12 6C10.1167 6 8.3875 6.49583 6.8125 7.4875C5.2375 8.47917 4.03333 9.81667 3.2 11.5C4.03333 13.1833 5.2375 14.5208 6.8125 15.5125C8.3875 16.5042 10.1167 17 12 17Z" />
              )}
            </svg>
          </button>
        )}
        <div className="post__navigation">
          <button
            className={clsx("post__button")}
            disabled={page === 1}
            onClick={decreasePage}
          >
            <svg
              className="post__icon"
              viewBox="0 0 21 21"
              fill="currentcolor"
              xmlns="http://www.w3.org/2000/svg"
              aria-label={translate("poster.nav.prevPage")}
            >
              <path d="M13.9677 19.195L5.5 10.7273L13.9677 2.25952L15.4708 3.76255L8.50605 10.7273L15.4708 17.692L13.9677 19.195Z" />
            </svg>
          </button>

          <span>
            {page}/{PAGE_COUNT}
          </span>

          <button
            className={clsx("post__button")}
            disabled={page === PAGE_COUNT}
            onClick={increasePage}
          >
            <svg
              className="post__icon"
              viewBox="0 0 21 21"
              fill="currentcolor"
              xmlns="http://www.w3.org/2000/svg"
              aria-label={translate("poster.nav.nextPage")}
            >
              <path d="M7.03421 19.195L15.502 10.7273L7.03421 2.25952L5.53119 3.76255L12.4959 10.7273L5.53119 17.692L7.03421 19.195Z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
